import {Logger} from '@mamdev/cad.static-next-lib';
import {PubSub} from '@mamdev/cad.static-next-lib';
import {Config} from './config';
import Viewport from './viewport';

/**
 * Handles ads
 */
class Ads {
  /**
   * Initialize the service
   */
  initialize() {
    try {
      if (this.initialized) return;

      this.adService = window.AdService;
      this.ASConfig = Config.get('AdServiceConfig');

      // Selector for ad containers which haven't been processed yet, excluding the ones generated by the adservice for adblock detection
      this.adContainerSelector = '.ad-container:not([data-processed="true"]):not([id*="adservice-abd-"])';

      // Ad slots which should be refreshed on ad refresh
      this.refreshableAdSlots = [
        'box_1',
        'top',
        'right'
      ];

      this.adService.setDefaultAdServer('google');
      this.adService.setConfig({
        'secure': true,
        'adservice-iframe-script-url': window.ui.AdServiceConfig.adServiceConnectorUrl,
        'request-ad-only-if-visible': false
      });

      this.adService.AdServer('google').setParam(this.ASConfig.init).setSizes(this.ASConfig.configurations.sizes);
      this.adService.setExclusionRuleSet(this.ASConfig.exclusionRules);
      this.adService.setAuctionConfig(this.ASConfig.prebidRules);

      this.adService.on('adblock.detectionfinished', () => {
        try {
          const result = this.adService.info.abd();
          console.log('adblock.detectionfinished', result);

          // Set detection result to the hidden field in the login form
          const abdInfoField = /** @type {HTMLInputElement} */ (document.getElementById('mod-loginform-ibaInfo'));

          if (abdInfoField) abdInfoField.value = 'abd=' + result.isBlocked;

          this.publish('ad:adBlockerActive', result);
          this.sendAdblockPixel(result);
        } catch (e) {
        }
      });

      this.removeInvisibleBoxSlots();

      // Get a list of all ad slots in the page, so that the AdService can force loading them all with a single request
      const singleRequestSlots = [];
      const allSlotsInDocument = document.querySelectorAll(this.adContainerSelector);
      allSlotsInDocument.forEach(slot => singleRequestSlots.push(slot.getAttribute('data-adservice-slot')));

      console.log('[ads] single request slots', singleRequestSlots);

      this.adService.AdServer('google').setConfig({
        safeFrameConfig: {
          sandbox: true
        },

        // Determine which ads should be requested simultaneously with a single request to the adservice
        singleRequest: true,
        singleRequestSlots: singleRequestSlots
      });

      // Swap taboola ad with box_1 ad. Only on mail.com and mobile view
      if (window.ui.tenant === 'mailcom' && Viewport.isMobile()) {
        this.swapTaboolaAndBox1();
      }

      // handling internal events
      PubSub.subscribe('dynamicAdsNeeded', (/** @type Object */adContainersData) => {
        this.log('dynamicAdsNeeded');
        this.log(adContainersData);
        this.fillAdContainers(adContainersData);
      });

      PubSub.subscribe('gallery:AdRefresh', () => {
        this.refreshAds();
      });

      this.passColumns();

      Viewport.subscribe('resize', () => {
        this.passColumns();
      });

      this.fillAdContainers();

      this.initialized = true;

      this.log('initialized');

      PubSub.subscribe('ad:triggerAdServiceReady', () => {
        this.publish('ad:adServiceReady');
      });

      this.publish('ad:adServiceReady');
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Logs a message
   *
   * @param {string} message
   */
  log(message) {
    Logger.log('ads', message);
  }

  /**
   * Logs an error message
   *
   * @param {string} message
   */
  logError(message) {
    Logger.log('ads', message, false, console.error);
  }

  /**
   * Publish a message to static current
   * @param {string} message
   * @param {*} params
   */
  publish(message, params = {}) {
    PubSub.publish(message, params);
  }

  /**
   * Find (unfilled) ad-containers on the page and request ads for them
   *
   * @param {[]} [adList]
   */
  fillAdContainers(adList) {
    this.log('fill ad containers');

    adList = adList || [];

    const emptyAdContainers = (adList.length > 0) ? adList : document.querySelectorAll(this.adContainerSelector);

    for (let i = 0, len = emptyAdContainers.length; i < len; i++) {
      const adContainer = /** @type HTMLElement */ (emptyAdContainers[i]);

      // Avoid requesting an ad for the same slot more than once
      adContainer.dataset.processed = 'true';

      const slotID = adContainer.dataset.adserviceSlot;

      this.callAd(slotID);
    }
  }

  /**
   * Requests an ad
   *
   * @param {string} adSlot
   */
  callAd(adSlot) {
    this.log('Call ad ' + adSlot);
    // this.adService.AdSlot(containerId).setParam('maxwidth', this.getMaxWidthParam(adTagId, adContainer));
    this.adService.ad(adSlot);
  }

  /**
   * Refresh ad slots
   */
  refreshAds() {
    this.log('Refreshing ads...');

    this.refreshableAdSlots.forEach(slot => this.adService.refresh(slot));
  }

  /**
   * Removes invisible box ad slots from the DOM.
   */
  removeInvisibleBoxSlots() {
    try {
      ['box_1', 'box_2'].forEach(slotId => {
        const slot = /** @type {HTMLElement} */ (document.querySelector('[data-adservice-slot="' + slotId + '"]'));

        if (slot && !slot.offsetParent) {
          slot.remove();
        }
      });
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Sends a pixel to track adblock detection values
   * See https://dp-jira.1and1.com/browse/DII-5683
   * @param {*} result
   */
  sendAdblockPixel(result) {
    try {
      const pixelUrl = this.generateAdBlockPixelUrl(result);

      this.log('pixel url = ' + pixelUrl);

      (document.createElement('img')).src = pixelUrl;
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Generates the URL for the adblock tracking pixel
   * @param {*} result
   * @return {string}
   */
  generateAdBlockPixelUrl(result) {
    try {
      let pixelUrl = 'https://nct.ui-portal.de/s?';

      /*
       * Determine abd value
       * 0 = no adblock detected
       * 1 = direct adblock detected
       * 2 = indirect adblock detected
       * 3 = direct adblock and AcceptableAds
       * 4 = indirect adblock and FF-Privatemode
       */
      let adbValue = 0; // default, no adblock detected
      const detection = result.details.detection;

      if (result.isBlocked) {
        if (result.indirectBlocked) adbValue = 2;
        if (detection.ff_private.state === 'finished' && detection.ff_private.result) adbValue = 4;
        if (result.directBlocked) adbValue = 1;
        if (detection.acceptable.state === 'finished' && detection.acceptable.result) adbValue = 3;
      }

      const trackingValues = {
        brand: 'mailcom',
        country: 'int',
        var: 'default',
        type: 'hidden',
        _c: 0,
        ts: Date.now(),
        name: 'homepage.startseite.event.ovk',
        reco: 'control',
        adb: adbValue
      };

      const trackingValuesList = [];

      Object.keys(trackingValues).forEach(key => {
        trackingValuesList.push(key + '=' + trackingValues[key]);
      });

      pixelUrl += trackingValuesList.join('&');

      return pixelUrl;
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Swaps Taboola and Box1 positions on mobile viewports (CADMAILCOM-5279)
   */
  swapTaboolaAndBox1() {
    try {
      let taboolaContainer = document.getElementById('adservice-contentbox');

      if (!taboolaContainer) {
        // On logout page the Taboola placement still has the old markup
        taboolaContainer = /** @type {HTMLElement} */ (document.querySelector('[data-mod-name="taboolateasers"]'));
      }

      const box1Container = document.getElementById('adservice-box_1');

      if (!taboolaContainer || !box1Container) return;

      const box1Parent = box1Container.parentElement;

      taboolaContainer.parentElement.appendChild(box1Container);
      box1Parent.appendChild(taboolaContainer);
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Passes column amount to adservice
   */
  passColumns() {
    const x = window.innerWidth;
    if (x < 768) {
      window.AdService.AdServer('google').setParam('col', '1');
    } else if (x >= 768 && x < 1024) {
      window.AdService.AdServer('google').setParam('col', '2');
    } else if (x >= 1024 && x < 1420) {
      window.AdService.AdServer('google').setParam('col', '3');
    } else if (x >= 1420) {
      window.AdService.AdServer('google').setParam('col', '4');
    }
  }
}

export default new Ads();
